import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { Paper } from '@mui/material';
import {
  DataGrid,
  type GridRowId,
  type GridColDef,
  type DataGridProps,
  type GridValidRowModel,
} from '@mui/x-data-grid';

import { TxFooter } from './components/TxFooter';
import { TxToolbar } from './components/TxToolbar';
import { formatColumns } from './utils/formatColumns';

type BaseProps<T extends GridValidRowModel> = {
  rows?: T[];
  columns: GridColDef<T>[];
  getRowId?: (row: T) => GridRowId;
  onAdd?: () => void;
  onRefresh?: () => Promise<any> | void;
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  actionsDisplay?: 'inline' | 'menu';
  hideToolbar?: boolean;
  hideFooter?: boolean;
  hideFooterToolbar?: boolean;
  hidePageSizeSelector?: boolean;
  initialPageSize?: number;
  customPageSizeOptions?: number[];
};

// * Aqui estendemos* DataGridProps, removendo as que já definimos acima
export type TxDataGridClientProps<T extends GridValidRowModel = any> = BaseProps<T> &
  Omit<
    DataGridProps<T>,
    | 'rows'
    | 'columns'
    | 'getRowId'
    | 'pagination'
    | 'paginationModel'
    | 'onPaginationModelChange'
    | 'loading'
    | 'slots'
    | 'initialState'
  > & {
    extraToolbarButtons?: React.ReactNode;
    toolbarFields?: React.ReactNode;
  };

export function TxDataGridClient<T extends GridValidRowModel = any>({
  rows = [],
  columns: _columns,
  getRowId,
  onAdd,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  actionsDisplay,
  extraToolbarButtons,
  toolbarFields,
  hideToolbar = false,
  hideFooter = false,
  hideFooterToolbar = false,
  hidePageSizeSelector = false,
  initialPageSize = 25,
  customPageSizeOptions,
  ...dataGridProps
}: TxDataGridClientProps<T>) {
  // Memo: o DataGrid reprocessa/re-mede as colunas (esp. as flex) sempre que a
  // referência de `columns` muda. Sem memo, cada re-render do pai (ex.: SWR
  // revalidando no foco) recriava o array e piscava o grid.
  const columns = useMemo(
    () => formatColumns({ columns: _columns, onView, onEdit, onDelete, actionsDisplay }),
    [_columns, onView, onEdit, onDelete, actionsDisplay]
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: initialPageSize,
  });
  const [loading, setLoading] = useState(false);

  const isFirstLoad = useRef(true);
  const extraButtonsRef = useRef(extraToolbarButtons);

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    setLoading(true);
    try {
      await onRefresh();
    } finally {
      setLoading(false);
    }
  }, [onRefresh]);

  useEffect(() => {
    if (isFirstLoad.current) {
      handleRefresh();
      isFirstLoad.current = false;
    }
  }, [handleRefresh]);

  useEffect(() => {
    extraButtonsRef.current = extraToolbarButtons;
  }, [extraToolbarButtons]);

  const toolbar = useCallback(
    () => (
      <TxToolbar onAdd={onAdd} onRefresh={handleRefresh} fields={toolbarFields}>
        {extraButtonsRef.current}
      </TxToolbar>
    ),
    [onAdd, handleRefresh, toolbarFields]
  );

  // Espelho da correção do app do fisco (UI-2, VERIFICACAO §4 Causa A).
  //
  // Deixar o slot indefinido NÃO desliga o rodapé: para o `computeSlots` do MUI X
  // 7.28 (`internals/utils/computeSlots.js`) chave com valor `undefined` é pulada e
  // vale o padrão, ou seja o `GridFooter`. Toda tela que passava `hideFooter` e
  // desenhava o próprio `.pager` do canvas ficava com dois rodapés.
  //
  // `slots.footer = null` também não serve: o `GridFooterPlaceholder` faz
  // `jsx(rootProps.slots.footer, …)` e renderizar `null` como tipo de elemento
  // derruba a árvore. Quem desliga o rodapé de verdade é a prop `hideFooter` do
  // próprio DataGrid, repassada abaixo — o placeholder devolve `null` antes de
  // tocar no slot. Aqui o slot só deixa de ser trocado à toa.
  const slotsConfig: DataGridProps['slots'] = {};
  if (!hideToolbar) slotsConfig.toolbar = toolbar;
  if (!hideFooter) slotsConfig.footer = TxFooter;

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        // A prop que realmente desliga o rodapé — ver o comentário do `slotsConfig`.
        // `hideFooterToolbar` e `hidePageSizeSelector` seguem valendo só para o
        // `TxFooter`, quando ele existe.
        hideFooter={hideFooter}
        {...(!hideFooter && {
          pagination: true,
          paginationModel,
          onPaginationModelChange: setPaginationModel,
          pageSizeOptions: customPageSizeOptions || [10, 25, 50, 100, 200],
        })}
        initialState={{
          ...(!hideFooter ? { density: 'compact' } : { density: 'standard' }),
          pagination: { paginationModel: { pageSize: initialPageSize, page: 0 } },
        }}
        loading={loading}
        slots={slotsConfig}
        slotProps={{
          footer: { hideToolbar: hideFooterToolbar, hidePageSizeSelector } as any,
        }}
        sx={{
          // '& .MuiDataGrid-toolbarContainer': { padding: '0 10px' },
          // '& .MuiDataGrid-toolbarContainer > *': { fontWeight: 500 },
          // '& .MuiTablePagination-toolbar': { minHeight: '35px', height: 35 },
          '& .MuiTablePagination-toolbar > *': { margin: 1 },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'inherit !important',
          },
        }}
        {...dataGridProps}
      />
    </Paper>
  );
}
